import React, { useState } from "react";

import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import "pages/AdminPage.scss";

interface CashFund {
  id: string;
  customer: string;
  type: "gift_received" | "used" | "refund";
  amount: number;
  balance: number;
  description: string;
  createdAt: string;
}

const TYPE_LABEL: Record<string, string> = {
  gift_received: "선물 수령",
  used: "사용",
  refund: "환불",
};
const TYPE_BADGE: Record<string, "active" | "info" | "warning"> = {
  gift_received: "active",
  used: "info",
  refund: "warning",
};

const MOCK: CashFund[] = [
  {
    id: "1",
    customer: "박혜진",
    type: "gift_received",
    amount: 580000,
    balance: 580000,
    description: "[그룹선물] 웨딩 장식 세트",
    createdAt: "2024-03-01",
  },
  {
    id: "2",
    customer: "박혜진",
    type: "used",
    amount: -128000,
    balance: 452000,
    description: "플라워하우스 - 웨딩 꽃 장식 세트 구매",
    createdAt: "2024-03-05",
  },
  {
    id: "3",
    customer: "김수민",
    type: "gift_received",
    amount: 1200000,
    balance: 1200000,
    description: "[그룹선물] 프리미엄 침구",
    createdAt: "2024-03-02",
  },
  {
    id: "4",
    customer: "김수민",
    type: "used",
    amount: -380000,
    balance: 820000,
    description: "에버그린 - 프리미엄 침구 세트 구매",
    createdAt: "2024-03-10",
  },
  {
    id: "5",
    customer: "정아름",
    type: "gift_received",
    amount: 890000,
    balance: 890000,
    description: "[그룹선물] 스마트홈 패키지",
    createdAt: "2024-03-03",
  },
];

const CashFundManagement: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const filtered = MOCK.filter((c) => !search || c.customer.includes(search));

  const columns: Column<CashFund>[] = [
    { key: "customer", title: "고객명", width: "100px" },
    {
      key: "type",
      title: "유형",
      width: "110px",
      render: (v) => <Badge variant={TYPE_BADGE[v]}>{TYPE_LABEL[v]}</Badge>,
    },
    {
      key: "amount",
      title: "금액",
      width: "120px",
      render: (v) => (
        <span style={{ color: v > 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>
          {v > 0 ? "+" : ""}
          {v.toLocaleString()}원
        </span>
      ),
    },
    {
      key: "balance",
      title: "잔여 캐시",
      width: "120px",
      render: (v) => `${v.toLocaleString()}원`,
    },
    { key: "description", title: "내용" },
    { key: "createdAt", title: "일시", width: "110px" },
  ];

  const customerMap = new Map(MOCK.map((m) => [m.customer, m.balance]));
  const totalBalance = Array.from(customerMap.values()).reduce((a, b) => a + b, 0);

  return (
    <div className="admin-page">
      <ContentHeader
        title="캐시 관리"
        description="웨딩 캐시(선물 금액) 내역을 조회합니다."
        actions={[
          <Button key="export" variant="outline">
            엑셀 다운로드
          </Button>,
        ]}
      />

      <div className="stat-cards">
        <div className="stat-card stat-card--primary">
          <div className="stat-card__label">총 캐시 잔액</div>
          <div className="stat-card__value">{totalBalance.toLocaleString()}원</div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card__label">캐시 수령 건수</div>
          <div className="stat-card__value">{MOCK.filter((m) => m.type === "gift_received").length}건</div>
        </div>
        <div className="stat-card stat-card--info">
          <div className="stat-card__label">캐시 사용 건수</div>
          <div className="stat-card__value">{MOCK.filter((m) => m.type === "used").length}건</div>
        </div>
      </div>

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="고객명 검색" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} prefix="🔍" />
        </div>
        <Table columns={columns} data={filtered} rowKey="id" />
        <Pagination current={currentPage} total={filtered.length} onChange={setCurrentPage} />
      </div>
    </div>
  );
};

export default CashFundManagement;
